import axios from 'axios'
import aqe from '@pushly/aqe'
import { Singleton } from 'typescript-ioc/es5'
import { IApiCallOptions, IApiResponse } from '@pushly/aqe/lib/interfaces'
import { AppService } from './app.service'
import { Container } from 'typescript-ioc/es5'
import querystring from 'query-string'
import { IAccessPolicy } from '../interfaces/access-policy'
import { IUserRole } from '@halo/interfaces/user-role'

@Singleton
class AccessPolicyService {
    protected readonly appService: AppService

    public constructor() {
        this.appService = Container.get(AppService)
    }

    public async fetchAll(opts: IApiCallOptions = {}): Promise<IApiResponse<IAccessPolicy[]>> {
        const response: IApiResponse<IAccessPolicy[]> = {
            ok: false,
            data: [],
            meta: {},
        }

        if (opts?.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const options = querystring.stringify(opts.query || {})
            const res = await axios.get(`${aqe.defaults.publicApiDomain}/v3/access-control/access-policies?${options}`)

            const { data, ...meta } = res.data

            response.ok = true
            response.data = data
            response.meta = meta
        } catch (err) {
            response.error = err

            if (process.env.IS_LOCAL) {
                console.debug('access_policy_fetch_error', err)
            }
        }

        if (opts?.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return response
    }

    public async fetch(id: number, opts: IApiCallOptions = {}): Promise<IApiResponse<IAccessPolicy>> {
        const response: IApiResponse<IAccessPolicy> = {
            ok: false,
            data: null,
            meta: {},
        }

        if (opts?.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const options = querystring.stringify(opts.query || {})
            const res = await axios.get(
                `${aqe.defaults.publicApiDomain}/v3/access-control/access-policies/${id}?${options}`,
            )

            const { data, ...meta } = res.data

            response.ok = true
            response.data = data
            response.meta = meta
        } catch (err) {
            response.error = err

            if (process.env.IS_LOCAL) {
                console.debug('access_policy_fetch_error', err)
            }
        }

        if (opts?.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return response
    }

    public async patch(
        id: number,
        updates: Partial<IAccessPolicy>,
        opts: IApiCallOptions = {},
    ): Promise<IApiResponse<IAccessPolicy>> {
        const response: IApiResponse<IAccessPolicy> = {
            ok: false,
            data: null,
            meta: {},
        }

        if (opts?.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const res = await axios.patch(`${aqe.defaults.publicApiDomain}/access-control/access-policies/${id}`, {
                id,
                ...updates,
            })

            const { data, ...meta } = res.data

            response.ok = true
            response.data = data
            response.meta = meta
        } catch (err) {
            response.error = err

            if (process.env.IS_LOCAL) {
                console.debug('access_policy_patch_error', err)
            }
        }

        if (opts?.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return response
    }

    public async create(
        policy: Partial<IAccessPolicy>,
        opts: IApiCallOptions = {},
    ): Promise<IApiResponse<IAccessPolicy>> {
        const response: IApiResponse<IAccessPolicy> = {
            ok: false,
            data: null,
            meta: {},
        }

        if (opts?.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const res = await axios.post(`${aqe.defaults.publicApiDomain}/access-control/access-policies`, policy)

            const { data, ...meta } = res.data

            response.ok = true
            response.data = data
            response.meta = meta
        } catch (err) {
            response.error = err

            if (process.env.IS_LOCAL) {
                console.debug('access_policy_post_error', err)
            }
        }

        if (opts?.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return response
    }

    public async attachUserRoleIds(
        id: number,
        roleIds: number[],
        opts: IApiCallOptions = {},
    ): Promise<IApiResponse<IUserRole>> {
        const response: IApiResponse<null> = {
            ok: false,
            data: null,
            meta: {},
        }

        if (opts?.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            await axios.post(`${aqe.defaults.publicApiDomain}/access-control/access-policies/${id}/attach-user-roles`, {
                user_role_ids: roleIds,
            })

            response.ok = true
        } catch (err) {
            response.error = err

            if (process.env.IS_LOCAL) {
                console.debug('access_policy_attach_roles_error', err)
            }
        }

        if (opts?.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return response
    }

    public async removeUserRoleIds(
        id: number,
        roleIds: number[],
        opts: IApiCallOptions = {},
    ): Promise<IApiResponse<IUserRole>> {
        const response: IApiResponse<null> = {
            ok: false,
            data: null,
            meta: {},
        }

        if (opts?.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            await axios.post(`${aqe.defaults.publicApiDomain}/access-control/access-policies/${id}/remove-user-roles`, {
                user_role_ids: roleIds,
            })

            response.ok = true
        } catch (err) {
            response.error = err

            if (process.env.IS_LOCAL) {
                console.debug('access_policy_remove_roles_error', err)
            }
        }

        if (opts?.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return response
    }
}

export default AccessPolicyService
